import React from 'react';
import ReviewCardSkeleton from './skeleton/ReviewCardSkeleton';
import StarRating from './StarRating';
import { PlatformRating } from '../type';
import iconBell from '../../assets/IconBell.svg';
import iconRingBell from '../../assets/IconRingBell.svg';
import iconStar from '../../assets/IconStar.svg';
import iconStarGreen from '../../assets/IconStarGreen.svg';
import iconThumbGreen from '../../assets/IconThumbGreen.svg';
import iconCommentStar from '../../assets/IconCommentStar.svg';

interface NetworkCardProps {
    network: 'google_my_business' | 'facebook' | 'opinion_system';
    isConnected: boolean;
    isLoading: boolean;
    rating?: PlatformRating;
    unreadCount: number;
    logo: string;
    name: string;
    onConnect: () => void;
    onCardClick: (network: string, unreadCount: number) => void;
}

export const NetworkCard: React.FC<NetworkCardProps> = ({
    network,
    isConnected,
    isLoading,
    rating,
    unreadCount,
    logo,
    name,
    onConnect,
    onCardClick,
}) => {
    const getCardStyles = (unreadCount: number, baseStyles: string) => {
        if (unreadCount > 0) {
            return `${baseStyles} cursor-pointer hover:shadow-lg hover:outline-2 hover:outline-[var(--primary-color)] transition-all duration-200 relative`;
        }
        return baseStyles;
    };

    const unreadCountIconToDisplay = (unreadCount: number) => {
        return unreadCount > 0 ? (
            <div className="w-10 h-10 p-1.5 bg-red-500 rounded-[60px] inline-flex flex-col justify-center items-center gap-2.5">
                <div className="w-5 h-5 relative">
                    <img className="absolute" src={iconRingBell} alt="icon ring bell" />
                </div>
            </div>
        ) : (
            <div className="w-10 h-10 p-1.5 bg-neutral-100 rounded-[60px] inline-flex flex-col justify-center items-center gap-2.5">
                <div className="w-5 h-5 relative">
                    <div className="w-4 h-5 left-[2.04px] top-0 absolute" />
                    <img className="absolute" src={iconBell} alt="icon bell" />
                </div>
            </div>
        );
    };

    if (isLoading) {
        return <ReviewCardSkeleton />;
    }

    if (!isConnected) {
        return (
            <div className="w-full min-w-[224px] xl:w-[32%] self-stretch p-3 lg:p-4 xl:p-5 bg-zinc-100 rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start items-center">
                <div className="self-stretch flex-1 flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch flex-1 flex flex-col justify-start items-start gap-6">
                        <div className="self-stretch rounded-xl inline-flex justify-start items-center gap-2">
                            <div className="">
                                <img className="w-6 h-6" src={logo} alt={`logo ${name}`} />
                            </div>
                            <div className="flex-1 justify-center text-stone-900 text-sm 2xl:text-lg font-semibold font-['Montserrat'] leading-snug">
                                {`Connectez votre compte ${name} !`}
                            </div>
                        </div>
                        <div className="self-stretch justify-center text-black text-[10px] 2xl:text-xs font-normal font-['Montserrat'] leading-tight">
                            Affichez et gérez vos avis clients facilement. Un bon moyen de rester
                            visible et réactif !
                        </div>
                    </div>
                    <div
                        className="self-stretch px-5 py-2.5 bg-primary rounded-[65px] inline-flex justify-center items-center gap-1 cursor-pointer"
                        onClick={onConnect}
                    >
                        <div className="text-center justify-center text-neutral-700 text-[10px] 2xl:text-xs font-semibold font-['Montserrat'] leading-none">
                            Je le connecte
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={getCardStyles(
                unreadCount,
                'w-full min-w-[224px] xl:w-[32%] self-stretch p-3 lg:p-4 xl:p-5 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex flex-col justify-start items-start gap-2.5',
            )}
            onClick={() => onCardClick(network, unreadCount)}
        >
            <div className="self-stretch rounded-xl inline-flex justify-start items-center gap-2">
                <div className="w-6 h-6 relative overflow-hidden">
                    <img className="w-6 h-6" src={logo} alt={`logo ${name}`} />
                </div>
                <div className="flex-1 justify-center text-neutral-900 text-base lg:text-lg font-semibold font-['Montserrat'] leading-6 lg:leading-7">
                    {name}
                </div>
            </div>
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

            {rating?.totalReviews === 0 ? (
                <div className="self-stretch flex-1 flex flex-col items-start">
                    <div className="self-stretch inline-flex justify-start items-center gap-2">
                        <div className="p-1.5 bg-zinc-100 rounded-[60px] inline-flex flex-col justify-start items-start gap-2.5">
                            <div className="w-7 h-7 relative overflow-hidden">
                                <img className="absolute" src={iconStar} alt="icon star" />
                            </div>
                        </div>
                        <div className="inline-flex flex-col justify-start items-start gap-0.5">
                            <div className="justify-center text-stone-900 text-lg font-semibold font-['Montserrat'] leading-snug">
                                Aucun avis
                            </div>
                        </div>
                    </div>
                    <div className="self-stretch justify-center text-neutral-700 text-xs 2xl:text-sm font-normal font-['Montserrat'] mt-2">
                        {`Pas encore d'avis ${name} ? Encouragez vos clients à s'exprimer pour booster votre visibilité et faire la différence ! 😉`}
                    </div>
                </div>
            ) : (
                <div className="self-stretch flex-1 flex flex-col justify-between items-start">
                    <div className="self-stretch inline-flex justify-start items-center gap-2 mb-5">
                        {network === 'facebook' ? (
                            <>
                                <div className="w-10 h-10 p-1.5 bg-green-100 rounded-[60px] inline-flex flex-col justify-center items-center gap-2.5">
                                    <div className="w-5 h-5 relative">
                                        <img
                                            className="absolute"
                                            src={iconThumbGreen}
                                            alt="icon thumb"
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                                    <div className="self-stretch justify-center text-neutral-500 text-sm font-semibold font-['Montserrat'] leading-tight">
                                        Recommandation
                                    </div>
                                    <div className="self-stretch inline-flex justify-start items-center gap-1">
                                        <div className="justify-center text-neutral-900 text-lg font-semibold font-['Montserrat'] leading-7">
                                            {rating ? rating.rating + '%' : '-'}
                                        </div>
                                        <div className="w-5 h-5 relative overflow-hidden">
                                            <img
                                                className="absolute"
                                                src={iconCommentStar}
                                                alt="icon comment star"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="w-10 h-10 p-1.5 bg-green-100 rounded-[60px] inline-flex flex-col justify-center items-center gap-2.5">
                                    <div className="w-5 h-5 relative">
                                        <img
                                            className="absolute"
                                            src={iconStarGreen}
                                            alt="icon star"
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                                    <div className="self-stretch justify-center text-neutral-500 text-sm font-semibold font-['Montserrat'] leading-tight">
                                        Note
                                    </div>
                                    <div className="self-stretch inline-flex justify-start items-center gap-1 2xl:gap-2">
                                        <div className="flex justify-start items-start gap-0.5 2xl:gap-1">
                                            <div className="justify-center text-neutral-900 text-sm 2xl:text-lg font-semibold font-['Montserrat'] leading-tight 2xl:leading-7">
                                                {rating?.rating.toFixed(1).replace('.', ',')}
                                            </div>
                                            <div className="justify-center text-neutral-900 text-sm 2xl:text-lg font-normal font-['Montserrat'] leading-tight 2xl:leading-7">
                                                /5
                                            </div>
                                        </div>
                                        <div className="flex justify-start items-center gap-1">
                                            <div className="w-4 h-4 2xl:w-5 2xl:h-5 relative">
                                                <StarRating
                                                    rating={rating?.rating || 0}
                                                    iconSize={'h-4 2xl:h-5'}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex w-full min-h-[60px]">
                        <div className="flex flex-col w-1/2 justify-between">
                            <div className="self-stretch text-neutral-500 text-sm font-normal font-['Montserrat'] leading-tight mb-2">
                                Total avis
                            </div>
                            <div className="self-stretch justify-center text-neutral-900 text-lg font-semibold font-['Montserrat'] leading-7">
                                {rating?.totalReviews || 0}
                            </div>
                        </div>
                        <div className="border-l border-[#E5E5E5] flex flex-col w-1/2">
                            <div className="self-stretch text-neutral-500 text-sm font-normal font-['Montserrat'] leading-tight flex justify-end mb-2">
                                Non lus
                            </div>
                            <div className="flex justify-between">
                                <span className="m-auto">
                                    {unreadCountIconToDisplay(unreadCount)}
                                </span>
                                <div className="self-end justify-center text-neutral-900 text-lg font-semibold font-['Montserrat'] leading-7">
                                    {unreadCount}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
